// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
// Others
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_CLIENT,
  EMPTY_STRING,
  EMPTY_VALUE,
  FILTER_DEFAULT_VALUES,
  optionBusinessTypeClient,
  optionsTypePage,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IClient, IQueryParamClient } from '~/utils/interface/client';
import { deleteClient, getListClient } from '~/thunks/client/clientThunk';
import { DateFormatEnum, RoleEnum, TypeCrmEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IPaginationResponse } from '~/utils/interface/base';
import { formatCurrencyUSD, formatDate, formatPhoneNumber, getFullName } from '~/utils/helper/common';
import { clientActions, selectIsRefreshListClient } from '~/thunks/client/clientSlice';
import {
  IBaseMoreAction,
  IBaseOption,
  IFilterValueChange,
  IHeaderCellType,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { CLIENT_PERMISSIONS, LEAD_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './ClientList.module.scss';
import { icons } from '~/assets';

type Props = {
  isTypePage?: string;
  typeList?: TypeCrmEnum;
  onChangeType?: (value: string) => void;
};

const cx = classNames.bind(styles);

const columnsClient = (
  t: TFunction,
  sortBy: ISortBy[],
  typeList: TypeCrmEnum,
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IClient>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    ...(typeList === TypeCrmEnum.CLIENT
      ? [
          {
            id: 'id',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_client_id')}</Box>
                <SortComponent name='id' sortType={getSortType('id')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => record?.id,
            minWidth: 120,
          },
          {
            id: 'client',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_email')}</Box>
                <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => {
              return (
                <Stack direction={'column'}>
                  <Typography className={cx('textName')}>
                    {getFullName({
                      firstName: record?.firstName,
                      lastName: record?.lastName,
                    })}
                  </Typography>
                  <Typography className={cx('textEmail')}>{record?.clientContacts?.[0]?.email}</Typography>
                </Stack>
              );
            },
          },
          {
            id: 'address',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_address')}</Box>
                <SortComponent name='address' sortType={getSortType('address')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => record?.address?.address || EMPTY_VALUE,
            minWidth: 200,
          },
          {
            id: 'amount',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_amount')}</Box>
                <SortComponent name='amount' sortType={getSortType('amount')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => {
              return (
                <Typography className={cx('textPrice')}>{formatCurrencyUSD({ value: record?.amount })}</Typography>
              );
            },
          },
          {
            id: 'type',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_type')}</Box>
                <SortComponent name='type' sortType={getSortType('type')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) =>
              record?.businessType ? <BaseStatus label={record?.businessType || EMPTY_STRING} /> : EMPTY_VALUE,
          },

          {
            id: 'assigned',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_assigned_to')}</Box>
                <SortComponent name='assigned to' sortType={getSortType('assigned to')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => {
              return (
                <Stack direction={'column'}>
                  <Typography className={cx('textName')}>
                    {getFullName({
                      firstName: record?.assignedUser?.firstName,
                      lastName: record?.assignedUser?.lastName,
                    })}
                  </Typography>
                  <Typography className={cx('textEmail')}>{record?.assignedUser?.email}</Typography>
                </Stack>
              );
            },
            minWidth: 170,
          },
          {
            id: 'lastContact',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_last_contacted')}</Box>
                <SortComponent name='last contacted' sortType={getSortType('last contacted')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) =>
              record?.lastContact ? (
                <Typography className={cx('textDate')}>
                  {formatDate(record?.lastContact, DateFormatEnum.MM_DD_YYYY)}
                </Typography>
              ) : (
                EMPTY_VALUE
              ),
            minWidth: 160,
          },
          {
            id: 'due Date',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_follow_up_due')}</Box>
                <SortComponent name='follow up due' sortType={getSortType('follow up due')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) =>
              record?.dueDate ? (
                <Typography className={cx('textDate')}>
                  {formatDate(record?.dueDate, DateFormatEnum.MM_DD_YYYY)}
                </Typography>
              ) : (
                EMPTY_VALUE
              ),
            minWidth: 160,
          },
          {
            id: 'status',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_status')}</Box>
                <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) =>
              record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_VALUE,
          },
        ]
      : []),

    ...(typeList === TypeCrmEnum.LEAD
      ? [
          {
            id: 'id',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('lead_title_lead_id')}</Box>
                <SortComponent name='id' sortType={getSortType('id')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => record?.id,
            minWidth: 120,
          },
          {
            id: 'client',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('lead_title_lead_name')}</Box>
                <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => {
              return (
                <Typography className={cx('textName')}>
                  {getFullName({
                    firstName: record?.firstName,
                    lastName: record?.lastName,
                  })}
                </Typography>
              );
            },
          },
          {
            id: 'companyName',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('lead_title_company_name')}</Box>
                <SortComponent name='company name' sortType={getSortType('company name')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => record?.companyName || EMPTY_VALUE,
            minWidth: 200,
          },
          {
            id: 'phoneNumber',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('lead_title_phone_number')}</Box>
                <SortComponent name='phone number' sortType={getSortType('phone number')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => formatPhoneNumber(record?.clientContacts?.[0]?.phoneNumber) || EMPTY_VALUE,
            minWidth: 200,
          },
          {
            id: 'address',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('table_header_title_address')}</Box>
                <SortComponent name='address' sortType={getSortType('address')} handleSort={handleSort} />
              </Stack>
            ),
            render: (record: IClient) => record?.address?.address || EMPTY_VALUE,
            minWidth: 200,
          },
        ]
      : []),

    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IClient) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id),
                },
              ]
            : []),

          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),

          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onDelete(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const ClientList = (props: Props) => {
  //#region Destructuring Props
  const { isTypePage, typeList, onChangeType } = props;

  const { canViewLeads, canCreateLeads, canDetailLeads, canEditLeads, canDeleteLeads } =
    usePermissions(LEAD_PERMISSIONS);
  const { canViewClients, canCreateClients, canDetailClients, canEditClients, canDeleteClients } =
    usePermissions(CLIENT_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListClient = useSelector(selectIsRefreshListClient);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_CLIENT);
  const [filterParams, setFilterParams] = useState<IQueryParamClient>(FILTER_DEFAULT_VALUES);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [paramObject, setParamObject] = useState<IQueryParamClient>({
    page: pageSelected,
    limit: limitPage,
    type: typeList,
  });

  const [clientList, setClientList] = useState<IClient[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [idClient, setIdClient] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListClient(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshListClient) return;

    handleGetListClient(paramObject);

    dispatch(clientActions.setRefreshClientList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListClient]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListClient = (params: IQueryParamClient) => {
    setIsLoading(true);

    dispatch(getListClient(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setClientList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddClient = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? adminRouteAbsolute.createClient : adminRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? salesAdminRouteAbsolute.createClient : salesAdminRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
      case RoleEnum.SALES_PERSON:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? salesPersonRouteAbsolute.createClient
              : salesPersonRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? operationsManagerRouteAbsolute.createClient
              : operationsManagerRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? warehouseLeadRouteAbsolute.createClient
              : warehouseLeadRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? warehouseStaffRouteAbsolute.createClient
              : warehouseStaffRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
      case RoleEnum.FOREMAN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? foremanRouteAbsolute.createClient : foremanRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
      case RoleEnum.CREW:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? crewRouteAbsolute.createClient : crewRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
      case RoleEnum.LOADER:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? loaderRouteAbsolute.createClient : loaderRouteAbsolute.createLead
          }?type=${typeList}`
        );
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? adminRouteAbsolute.updateClient(id) : adminRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? salesAdminRouteAbsolute.updateClient(id)
              : salesAdminRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.SALES_PERSON:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? salesPersonRouteAbsolute.updateClient(id)
              : salesPersonRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? operationsManagerRouteAbsolute.updateClient(id)
              : operationsManagerRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? warehouseLeadRouteAbsolute.updateClient(id)
              : warehouseLeadRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? warehouseStaffRouteAbsolute.updateClient(id)
              : warehouseStaffRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.FOREMAN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? foremanRouteAbsolute.updateClient(id)
              : foremanRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.CREW:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? crewRouteAbsolute.updateClient(id) : crewRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.LOADER:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? loaderRouteAbsolute.updateClient(id) : loaderRouteAbsolute.updateLead(id)
          }?type=${typeList}`
        );
        break;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdClient(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdClient(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteClient(idClient))
      .unwrap()
      .then(() => {
        setIdClient(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(clientActions.setRefreshClientList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? adminRouteAbsolute.detailClient(id) : adminRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.createInvoice);
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? adminRouteAbsolute.detailClient(id) : adminRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.SALES_PERSON:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? salesPersonRouteAbsolute.detailClient(id)
              : salesPersonRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? operationsManagerRouteAbsolute.detailClient(id)
              : operationsManagerRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? warehouseLeadRouteAbsolute.detailClient(id)
              : warehouseLeadRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? warehouseStaffRouteAbsolute.detailClient(id)
              : warehouseStaffRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.FOREMAN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? foremanRouteAbsolute.detailClient(id)
              : foremanRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.CREW:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? crewRouteAbsolute.detailClient(id) : crewRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.LOADER:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT ? loaderRouteAbsolute.detailClient(id) : loaderRouteAbsolute.detailLead(id)
          }?type=${typeList}`
        );
        break;
    }
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const fetchOptions = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsTypePage,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionBusinessTypeClient,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleFilterStatusChange = (
    businessType: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamClient>) => void
  ) => {
    if (!businessType) return;

    const { value } = businessType;

    onChange?.({ name: 'businessType', value: value });
    setFilterParams({ ...filterParams, businessType: value });
  };

  const handleApplyFilter = (values: IQueryParamClient | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        businessType: values?.businessType,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      businessType: undefined,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Box id='clientListComponent' className={cx('container')}>
      <BaseToolbar
        title={t(typeList === TypeCrmEnum.CLIENT ? 'client_title_clients_board' : 'lead_title_leads_board')}
        total={t(typeList === TypeCrmEnum.CLIENT ? 'client_title_total_clients' : 'lead_title_total_leads', {
          total: (typeList === TypeCrmEnum.CLIENT ? canViewClients : canViewLeads) ? totalItems : 0,
        })}
      >
        {(typeList === TypeCrmEnum.CLIENT ? canCreateClients : canCreateLeads) && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='outlined'
              className={cx('button')}
              onClick={handleAddClient}
              startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
            >
              {t(typeList === TypeCrmEnum.CLIENT ? 'client_title_add_client' : 'lead_title_add_lead')}
            </Button>
          </Box>
        )}

        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {typeList === TypeCrmEnum.CLIENT && (
          <BaseFilters<IQueryParamClient>
            value={filterParams}
            defaultValue={FILTER_DEFAULT_VALUES}
            onApply={handleApplyFilter}
            onClear={handleClearFilter}
          >
            {({ value, onChange }) => (
              <Stack direction={'column'} width={340} mb={4} gap={6}>
                <BaseSelect
                  gap={10}
                  height={40}
                  styleType='contained'
                  label={t('inventory_title_filter_type')}
                  placeholder={t('inventory_title_filter_all_type')}
                  callApiFn={fetchOptionFilter}
                  value={value?.businessType}
                  onChange={(option) => handleFilterStatusChange(option as any, onChange)}
                />
              </Stack>
            )}
          </BaseFilters>
        )}

        {/* {isTypePage && (
          <BaseSelect
            gap={4}
            height={39}
            width={130}
            styleType='outlined'
            placeholder={t('common_text_placeholder_select', { ns: 'common/base' })}
            callApiFn={fetchOptions}
            value={isTypePage}
            onChange={(item) => onChangeType?.(item?.value || EMPTY_STRING)}
          />
        )} */}
      </BaseToolbar>

      <Box className={cx('body')}>
        <BaseTable
          dataSource={(typeList === TypeCrmEnum.CLIENT ? canViewClients : canViewLeads) ? clientList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsClient(
            t,
            sortBy,
            typeList!,
            handleSort,
            handleView,
            handleEdit,
            handleDelete,
            typeList === TypeCrmEnum.CLIENT ? canDetailClients : canDetailLeads,
            typeList === TypeCrmEnum.CLIENT ? canEditClients : canEditLeads,
            typeList === TypeCrmEnum.CLIENT ? canDeleteClients : canDeleteLeads
          )}
          onPageChange={handlePageChange}
          {...((typeList === TypeCrmEnum.CLIENT ? canDetailClients : canDetailLeads) ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default ClientList;
